import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import Pagination from "components/Pagination/Pagination";
import FilterSidebar from "components/FilterSidebar/FilterSidebar";
import SearchBar from "components/SearchBar/SearchBar";
import formatField from "../../utils/formatFields";
import { useDispatch, useSelector } from "react-redux";
import { fetchDataRequest } from "../../redux/api/apiSlice";
import {
  resetFiltersAndSearch,
  searchQueryChanged,
  setSelectedFilters,
} from "../../redux/searchAndFilter/searchAndFilterSlice";
import styles from "../Datasets/Datasets.module.css";

const DATASET_NAME = "LLs";

const LessonsLearned = () => {
  const dispatch = useDispatch();
  const { datasets, loading, error } = useSelector((state) => state.api); // Extract dataset, loading, and error states from Redux store
  const lessonsLearned = datasets?.[DATASET_NAME]?.["All"] || []; //Get data for the specific dataset
  const { selectedFilters, searchQuery } = useSelector(
    (state) => state.searchFilter
  );
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch dataset when the component is mounted or lessons learned data changes fr
  useEffect(() => {
    if (!lessonsLearned.length) {
      dispatch(fetchDataRequest(DATASET_NAME));
    }
  }, [dispatch, lessonsLearned]);

  // Handle changes to filters (update Redux state with selected filters)
  const handleFilterChange = (category, value, dataset) => {
    dispatch(setSelectedFilters({ category, value, dataset }));
  };

  // Handle changes to the search query (update Redux state and reset pagination to first page)
  const handleSearchChange = (event) => {
    const newQuery = event.target.value;
    dispatch(searchQueryChanged(newQuery));
    setPage(1);
  };

  // Filter dataset based on selected filters and search query
  const filteredLessons = lessonsLearned.filter((lesson) => {
    const datasetFilters = selectedFilters[DATASET_NAME];
    const matchesSelectedFilters = datasetFilters
      ? Object.keys(datasetFilters).every((category) => {
          if (!datasetFilters[category]?.length) return true; // Skip if no filters are selected for the category
          const projectValue = lesson[category] || "";
          return datasetFilters[category].some((filterValue) =>
            projectValue.includes(filterValue)
          );
        })
      : true;

    const matchesSearch = lesson["Description "]
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());

    return matchesSelectedFilters && matchesSearch;
  });

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLessons = filteredLessons.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleResetFilters = (dataset) => {
    dispatch(resetFiltersAndSearch(dataset));
  };

  return (
    <Box className={styles.mainContainer}>
      <FilterSidebar
        dataset={DATASET_NAME}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      <Box className={styles.contentArea}>
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />
        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : (
          paginatedLessons.map((lesson, index) => (
            <Card key={index} variant="outlined" className={styles.contentCard}>
              <CardContent>
                <Typography variant="h6">
                  {'"' + lesson["Description "] + '"'}
                </Typography>
                <Typography>
                  <strong>DMC step(s):</strong>{" "}
                  {formatField(lesson["DMC step(s) "])}
                </Typography>
                <Typography>
                  <strong>SYRI(s) associated:</strong>{" "}
                  {formatField(lesson["SYRI(s) associated "])}
                </Typography>
                <Typography>
                  <strong>References code(s):</strong>
                  <br />{" "}
                  {formatField(
                    lesson["References codes (column A of the DCT)"]
                  )}
                </Typography>
              </CardContent>
            </Card>
          ))
        )}

        <Pagination
          count={Math.ceil(filteredLessons.length / itemsPerPage)}
          page={page}
          onPageChange={handlePageChange}
        />
      </Box>
    </Box>
  );
};

export default LessonsLearned; 